import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Trophy, Calendar, CreditCard, Vote, ShieldAlert, Info, UserPlus } from "lucide-react";
import { notifApi } from "@/lib/api";
import { PageContainer, SectionTitle, EmptyState } from "@/components/shared";
import { StaggerGroup, StaggerItem, SkeletonList } from "@/components/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const CAT_ICON = { trophy: Trophy, event: Calendar, collecte: CreditCard, vote: Vote, discipline: ShieldAlert, member: UserPlus, general: Info };
const CAT_COLOR = { trophy: "bg-trophy-gold/15 text-trophy-gold", event: "bg-indigo-100 text-indigo-700", collecte: "bg-emerald-100 text-emerald-700", vote: "bg-secondary/15 text-secondary", discipline: "bg-rose-100 text-rose-700", member: "bg-primary/10 text-primary", general: "bg-muted text-primary" };
const CATS = [{ v: "all", l: "Tout" }, { v: "member", l: "Membres" }, { v: "trophy", l: "Trophées" }, { v: "event", l: "Évènements" }, { v: "collecte", l: "Cotisations" }, { v: "discipline", l: "Discipline" }];
const timeAgo = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }); } catch { return ""; } };

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");

  const load = async () => { setLoading(true); try { const r = await notifApi.list(cat); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [cat]);

  const markRead = async (id) => { await notifApi.read(id); load(); };
  const markAll = async () => { await notifApi.readAll(); load(); };

  return (
    <PageContainer className="max-w-3xl">
      <SectionTitle action={<Button variant="outline" size="sm" onClick={markAll} data-testid="notif-tout-lu-bouton"><CheckCheck className="mr-1.5 h-4 w-4" />Tout marquer lu</Button>}>Centre de notifications</SectionTitle>
      <Tabs value={cat} onValueChange={setCat}><TabsList className="flex-wrap">{CATS.map((c) => <TabsTrigger key={c.v} value={c.v} data-testid={`notif-tab-${c.v}`}>{c.l}</TabsTrigger>)}</TabsList></Tabs>

      {loading ? <SkeletonList count={5} /> : items.length === 0 ? <EmptyState title="Aucune notification" description="Vos notifications apparaîtront ici." icon={Bell} /> : (
        <StaggerGroup className="space-y-2">
          {items.map((n) => { const Icon = CAT_ICON[n.category] || Info; return (
            <StaggerItem key={n.id}>
              <Card className={cn("cursor-pointer transition-shadow duration-200 hover:shadow-md", !n.read && "border-secondary/40 bg-secondary/5")} onClick={() => { markRead(n.id); if (n.link) navigate(n.link); }} data-testid="notif-item">
                <CardContent className="flex items-start gap-3 p-4">
                  <div className={cn("rounded-full p-2", CAT_COLOR[n.category] || CAT_COLOR.general)}><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="font-medium">{n.title}</span>{!n.read && <span className="h-2 w-2 rounded-full bg-secondary" />}</div><p className="text-sm text-muted-foreground">{n.body}</p><div className="mt-1 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</div></div>
                  {!n.read && <Check className="h-4 w-4 text-muted-foreground" />}
                </CardContent>
              </Card>
            </StaggerItem>
          ); })}
        </StaggerGroup>
      )}
    </PageContainer>
  );
}
